// @ts-strict-ignore
import moment from 'moment';

import { DataMap, RemainingDosesOption } from 'mobx/stores';

import { isSameDay } from 'utils/DateUtils';

import { getMissedDosesReasonsText, getRegimenText } from 'utils/OralOncoUtils';

import MissedDosesReasons from 'models/MissedDosesReasons';
import { OralReportVersion, ReportQuestionAnswer } from 'models/OralReport/OralReport.shared';

import { OralQuestionTypeV1, OralReportV1 } from 'models/OralReport/OralReportV1';
import { OralQuestionTypeV2, OralReportV2 } from 'models/OralReport/OralReportV2';
import {
  ActiveMedicationAnswer,
  MissedDosesAnswer,
  OralQuestionTypeV3,
  OralReportV3,
  RemainingDosesAnswer
} from 'models/OralReport/OralReportV3';

import {
  ReportTableCellData,
  ReportTableCellDataColor,
  ReportTableIcon,
  ReportTableOralChemoSection,
  ReportTableSymptomSection,
  TableReportItem
} from './ReportTable.model';

type AnyQuestionType = OralQuestionTypeV1 | OralQuestionTypeV2 | OralQuestionTypeV3;
type AnyOralReport = OralReportV1 | OralReportV2 | OralReportV3;
type AnyAnswerValue = any;

const FIRST_PILL_UNKNOWN = 'Start of Cycle Date Unknown';
const I_DONT_KNOW = "I Don't know";

function getNewCycleTooltip(date: Date, oral: AnyOralReport): string {
  const { regimen, isCustom } = oral;
  const regimenStr = getRegimenText(regimen, isCustom);
  const dateStr = moment(date).format('MMM Do, YYYY');
  return `New ${regimenStr} Cycle: ${dateStr}`;
}

// new cycle report (created when first pill date is not the same as report date) contain only start of cycle date
export function addNewCycleReportData(
  oralChemoSection: ReportTableOralChemoSection,
  symptomsSection: ReportTableSymptomSection,
  report: TableReportItem
) {
  oralChemoSection.NewCycle.push({
    color: ReportTableCellDataColor.gray,
    iconContent: ReportTableIcon.pill,
    tooltip: getNewCycleTooltip(report.createdAt, report.answer.oral)
  });

  if (
    report.answer.version === OralReportVersion.V2 ||
    report.answer.version === OralReportVersion.V3
  ) {
    oralChemoSection.ActiveMedication.push(null);
    oralChemoSection.DosesRemaining.push(null);
  } else {
    oralChemoSection.MedReceived.push(null);
  }

  oralChemoSection.RegimenAdherence.push(null);
  oralChemoSection.ChangeInOtherMeds.push(null);
  oralChemoSection.MissedDose.push(null);

  Object.keys(symptomsSection).forEach((causeName: string) => {
    symptomsSection[causeName].push(null);
  });
}

function getNewCycleOralCellData(
  reportedOnDifferentDay: boolean,
  oral?: AnyOralReport
): ReportTableCellData {
  if (!oral) {
    return null;
  }

  const { newCycleDate } = oral;
  const unknownCycleDate =
    !newCycleDate &&
    ((oral as OralReportV1).firstPill?.isIDontKnow ||
      (oral as OralReportV2)[OralQuestionTypeV2.StartOfCycle]?.isIDontKnow ||
      (oral as OralReportV3)[OralQuestionTypeV3.StartOfCycle]?.isIDontKnow);
  let cellData: ReportTableCellData = null;

  const shouldDisplay = (!!newCycleDate || unknownCycleDate) && !reportedOnDifferentDay;
  if (shouldDisplay) {
    cellData = {
      color: unknownCycleDate ? ReportTableCellDataColor.purple : ReportTableCellDataColor.gray,
      iconContent: unknownCycleDate ? ReportTableIcon.question : ReportTableIcon.pill,
      tooltip: unknownCycleDate ? FIRST_PILL_UNKNOWN : getNewCycleTooltip(newCycleDate, oral)
    };
  }

  return cellData;
}

function getOralCellData<T = AnyAnswerValue, Q = AnyQuestionType>(
  answer?: ReportQuestionAnswer<T, Q>,
  tooltipData?: string
): ReportTableCellData {
  let cellData: ReportTableCellData = null;
  if (answer?.isAnswered) {
    cellData = {
      color: answer.isOk ? ReportTableCellDataColor.green : ReportTableCellDataColor.red,
      tooltip: tooltipData || answer.customMessage
    };
  }
  return cellData;
}

function getMissedDosesDisplayValue(value: number | MissedDosesAnswer): number | string {
  if (typeof value === 'number') {
    return value;
  }

  switch (value) {
    case MissedDosesAnswer.None:
      return '';
    case MissedDosesAnswer.OneOrTwo:
      return '1-2';
    case MissedDosesAnswer.ThreeOrFour:
      return '3-4';
    case MissedDosesAnswer.MoreThanFour:
      return '>4';
    default:
      return value;
  }
}

function getOralMissedDosesCellData(
  missedDoses: ReportQuestionAnswer<number>,
  missedDosesReasonsMap: DataMap<MissedDosesReasons>
): ReportTableCellData {
  const cellData: ReportTableCellData = getOralCellData(missedDoses);
  if (missedDoses?.isAnswered && missedDoses.value > 0) {
    cellData.content = missedDoses.value;
    cellData.tooltip = getMissedDosesReasonsText(missedDoses, missedDosesReasonsMap);
  }
  return cellData;
}

function getActiveMedicationTooltipText(value?: boolean | ActiveMedicationAnswer): string {
  if (value === undefined) {
    return;
  }

  if (typeof value === 'boolean') {
    return Boolean(value) ? undefined : 'No';
  }

  return value === ActiveMedicationAnswer.NotReceived ? 'Not Received' : String(value);
}

function getOralDosesRemainingCellDataV3<T = AnyAnswerValue>(
  answer?: ReportQuestionAnswer<T, OralQuestionTypeV3>,
  tooltipData?: string
): ReportTableCellData {
  let cellData: ReportTableCellData = null;

  if (!answer) return null;

  if (answer.isAnswered) {
    cellData = getOralCellData(answer, tooltipData);
  } else if (answer.isIDontKnow) {
    cellData = {
      color: answer.isOk ? ReportTableCellDataColor.green : ReportTableCellDataColor.red,
      iconContent: ReportTableIcon.question,
      tooltip: I_DONT_KNOW
    };
  }

  return cellData;
}

function getOralMissedDosesCellDataV3(
  missedDoses: ReportQuestionAnswer<MissedDosesAnswer, OralQuestionTypeV3>,
  missedDosesReasonsMap: DataMap<MissedDosesReasons>
): ReportTableCellData {
  if (missedDoses?.isAnswered) {
    const cellData = getOralCellData(missedDoses);

    if (missedDoses.value !== MissedDosesAnswer.None && cellData) {
      cellData.content = getMissedDosesDisplayValue(missedDoses.value);
      cellData.tooltip = getMissedDosesReasonsText(missedDoses as any, missedDosesReasonsMap);
    }

    return cellData;
  }

  if (missedDoses?.isIDontKnow) {
    return {
      color: missedDoses.isOk ? ReportTableCellDataColor.green : ReportTableCellDataColor.red,
      iconContent: ReportTableIcon.question,
      tooltip: I_DONT_KNOW
    };
  }

  return null;
}

function getRemainingDosesTooltipText(value: RemainingDosesAnswer): string {
  switch (value) {
    case RemainingDosesAnswer.NoDoses:
      return 'None';
    case RemainingDosesAnswer.LowDoses:
      return '<1 Week';
    case RemainingDosesAnswer.WeeksDoses:
      return '1-2 Weeks';
    case RemainingDosesAnswer.ManyDoses:
      return '2+ Weeks';
    default:
      return undefined;
  }
}

export function addOralReportData(
  oralChemoSection: ReportTableOralChemoSection,
  report: TableReportItem,
  missedDosesReasonsMap: DataMap<MissedDosesReasons>,
  remainingDoseOptions: DataMap<RemainingDosesOption>
) {
  const { oral } = report.answer;
  const newCycleDate = oral?.newCycleDate;
  const reportedOnDifferentDay = newCycleDate && !isSameDay(newCycleDate, report.createdAt);

  let dataCell = getNewCycleOralCellData(reportedOnDifferentDay, oral as AnyOralReport);
  oralChemoSection.NewCycle.push(dataCell);

  if (report.answer.version === OralReportVersion.V2) {
    const oralReportV2 = oral as OralReportV2;
    dataCell = getOralCellData(oralReportV2?.[OralQuestionTypeV2.ActiveMedication]);
    oralChemoSection.ActiveMedication.push(dataCell);
    dataCell = getOralCellData(
      oralReportV2?.[OralQuestionTypeV2.DosesRemaining],
      remainingDoseOptions.get(oralReportV2?.[OralQuestionTypeV2.DosesRemaining]?.value)?.text
    );
    oralChemoSection.DosesRemaining.push(dataCell);
    dataCell = getOralCellData(oralReportV2?.[OralQuestionTypeV2.RegimenAdherence]);
    oralChemoSection.RegimenAdherence.push(dataCell);
    dataCell = getOralCellData(oralReportV2?.[OralQuestionTypeV2.OtherMedicationChange]);
    oralChemoSection.ChangeInOtherMeds.push(dataCell);

    dataCell = getOralMissedDosesCellData(oralReportV2?.missedDoses, missedDosesReasonsMap);
    oralChemoSection.MissedDose.push(dataCell);
  } else if (report.answer.version === OralReportVersion.V3) {
    const oralReportV3 = oral as OralReportV3;

    const activeMedication = oralReportV3?.[OralQuestionTypeV3.ActiveMedication];
    dataCell = getOralCellData(
      activeMedication,
      getActiveMedicationTooltipText(activeMedication.value)
    );
    oralChemoSection.ActiveMedication.push(dataCell);

    const dosesRemaining = oralReportV3?.[OralQuestionTypeV3.DosesRemaining];
    const dosesTooltip =
      dosesRemaining?.isAnswered && dosesRemaining.value
        ? getRemainingDosesTooltipText(dosesRemaining.value as RemainingDosesAnswer)
        : undefined;
    dataCell = getOralDosesRemainingCellDataV3(dosesRemaining, dosesTooltip);
    oralChemoSection.DosesRemaining.push(dataCell);

    dataCell = getOralCellData(oralReportV3?.[OralQuestionTypeV3.RegimenAdherence]);
    oralChemoSection.RegimenAdherence.push(dataCell);

    oralChemoSection.ChangeInOtherMeds.push(null);

    const missedDoses = oralReportV3?.[OralQuestionTypeV3.MissedDoses];
    dataCell = getOralMissedDosesCellDataV3(missedDoses, missedDosesReasonsMap);
    oralChemoSection.MissedDose.push(dataCell);
  } else {
    const oralReportV1 = oral as OralReportV1;
    dataCell = getOralCellData(oralReportV1?.medReceived);
    oralChemoSection.MedReceived.push(dataCell);
    dataCell = getOralCellData(oralReportV1?.regimenAsPrescribed);
    oralChemoSection.RegimenAdherence.push(dataCell);
    dataCell = getOralCellData(oralReportV1?.changesInOtherMeds);
    oralChemoSection.ChangeInOtherMeds.push(dataCell);

    dataCell = getOralMissedDosesCellData(oralReportV1?.missedDoses, missedDosesReasonsMap);
    oralChemoSection.MissedDose.push(dataCell);
  }
}

// if at least one report has oral data - oral section will be displayed
export function displayOralSection(oralChemoSection: ReportTableOralChemoSection) {
  return (
    oralChemoSection.ChangeInOtherMeds.some((dataCell) => dataCell !== null) ||
    oralChemoSection.MedReceived.some((dataCell) => dataCell !== null) ||
    oralChemoSection.MissedDose.some((dataCell) => dataCell !== null) ||
    oralChemoSection.NewCycle.some((dataCell) => dataCell !== null) ||
    oralChemoSection.RegimenAdherence.some((dataCell) => dataCell !== null) ||
    oralChemoSection.ActiveMedication.some((dataCell) => dataCell !== null) ||
    oralChemoSection.DosesRemaining.some((dataCell) => dataCell !== null)
  );
}
